/**
 * Reminder badge counts for one property (COL-435).
 *
 * One hook behind the badge on PropertyCard, PropertyListRow,
 * OwnerPropertyCard and the Reminders tab trigger in PropertyDetail. Those
 * four used to carry four copies of the same rooms → apartments → bookings
 * query, each with its own "active tenant" predicate, and every copy only
 * knew about the CURRENT billing period — an unpaid previous month vanished
 * from the badge the moment the tenant paid the new month.
 *
 * `total` is what the badge shows: current-period overdue rows plus open
 * past-month rows, the same items the property's Reminders tab lists under
 * "Overdue", so badge and tab can no longer disagree.
 *
 *   overdue    = tenants occupying now, reminders enabled, current period not
 *                paid, at reminder stage ≥ 1 (computeCurrentStage).
 *   pastMonths = open ledger rows for months before each tenant's current
 *                period (selectOpenPastMonthRent — canonical paid rule).
 *
 * Owners can read bookings but not other tenants' `payments` rows (RLS), so
 * for an owner-portal viewer the past-month leg quietly resolves to 0.
 */
import { useRef } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fetchRentLedgerForBookings } from "@/lib/rent-ledger-fetch";
import {
  computeCurrentStage,
  isPaidCurrentPeriod,
} from "@/lib/rent-reminder-templates";
import {
  currentPeriodLedger,
  daysSince,
  selectOpenPastMonthRent,
  type RentLedgerRow,
} from "@/lib/rent-past-months";
import { isRentCollectionEligible } from "@/lib/rent-collection";
import type { PropertyListItem } from "@/types/property";

export interface PropertyReminderStats {
  /** Current-period overdue rows (stage ≥ 1). */
  overdue: number;
  /** Open past-month rows. */
  pastMonths: number;
  /** Current-period rows at final notice (stage 4). */
  inkasso: number;
  /** overdue + pastMonths — what the badge shows. */
  total: number;
}

const EMPTY: PropertyReminderStats = {
  overdue: 0,
  pastMonths: 0,
  inkasso: 0,
  total: 0,
};

export const PROPERTY_REMINDER_STATS_KEY = "property-reminder-stats";

interface BookingRow {
  id: number;
  room_id: number | null;
  payment_status: string | null;
  rent_due_date: number | null;
  last_payment_received_at: string | null;
  rent_paid_through: string | null;
  manually_marked_paid: boolean | null;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
  admin_archived_at: string | null;
  actual_move_in_date: string | null;
  move_in_date: string | null;
  actual_move_out_date: string | null;
  move_out_date: string | null;
}

export async function fetchPropertyReminderStats(
  propertyId: number,
  opts: { propertyRemindersEnabled?: boolean | null } = {}
): Promise<PropertyReminderStats> {
  const { data: roomsRes, error: roomsErr } = await supabase
    .from("rooms")
    .select("id, apartment_id")
    .eq("property_id", propertyId);
  // Throw (not EMPTY) on a genuine query failure: swallowing it here would
  // make react-query's own error state — the thing callers can distinguish
  // from a real "nothing overdue" zero — never fire.
  if (roomsErr) throw new Error(`Could not load rooms: ${roomsErr.message}`);
  const rooms = (roomsRes ?? []) as unknown as Array<{
    id: number;
    apartment_id: number | null;
  }>;
  const roomIds = rooms.map((r) => r.id);
  if (!roomIds.length) return EMPTY;
  const apartmentIdByRoom = new Map<number, number | null>();
  for (const r of rooms) apartmentIdByRoom.set(r.id, r.apartment_id);
  const apartmentIds = Array.from(
    new Set(rooms.map((r) => r.apartment_id).filter(Boolean) as number[])
  );

  // Per-unit suppression: apartment override → property default → on. The
  // property default is passed in by list callers that already hold the row;
  // the detail page fetches it.
  const [aptRes, propRes, bookingsRes] = await Promise.all([
    apartmentIds.length
      ? supabase
          .from("apartments")
          .select("id, rent_reminders_enabled")
          .in("id", apartmentIds)
      : Promise.resolve({ data: [] as unknown[], error: null }),
    opts.propertyRemindersEnabled === undefined
      ? supabase
          .from("properties")
          .select("rent_reminders_enabled")
          .eq("id", propertyId)
          .maybeSingle()
      : Promise.resolve({ data: null, error: null }),
    supabase
      .from("bookings")
      .select(
        "id, room_id, payment_status, rent_due_date, last_payment_received_at, rent_paid_through, manually_marked_paid, status, start_date, end_date, admin_archived_at, actual_move_in_date, move_in_date, actual_move_out_date, move_out_date"
      )
      .in("room_id", roomIds),
  ]);
  if (bookingsRes.error)
    throw new Error(`Could not load bookings: ${bookingsRes.error.message}`);
  const apartmentRemindersById = new Map<number, boolean | null>();
  for (const a of (aptRes.data ?? []) as Array<{
    id: number;
    rent_reminders_enabled: boolean | null;
  }>) {
    apartmentRemindersById.set(a.id, a.rent_reminders_enabled);
  }
  const propertyRemindersEnabled =
    opts.propertyRemindersEnabled !== undefined
      ? opts.propertyRemindersEnabled
      : ((propRes.data as { rent_reminders_enabled: boolean | null } | null)
          ?.rent_reminders_enabled ?? null);

  const now = new Date();
  const bookings = (bookingsRes.data ?? []) as unknown as BookingRow[];
  let overdue = 0;
  let inkasso = 0;
  // Bookings whose past months we track: occupying now + reminders enabled.
  const trackedIds = new Set<number>();
  const trackedBookings: BookingRow[] = [];
  const rentDueDayByBooking = new Map<number, number | null>();
  for (const b of bookings) {
    // Canonical active-tenant test — the same one the Reminders tab applies
    // (COL-198), so the badge counts exactly the rows the tab lists.
    if (
      !isRentCollectionEligible(
        {
          status: b.status ?? "",
          admin_archived_at: b.admin_archived_at,
          actual_move_in_date: b.actual_move_in_date,
          move_in_date: b.move_in_date,
          start_date: b.start_date,
          actual_move_out_date: b.actual_move_out_date,
          move_out_date: b.move_out_date,
          end_date: b.end_date,
        },
        now
      )
    )
      continue;
    const aptId =
      b.room_id != null ? (apartmentIdByRoom.get(b.room_id) ?? null) : null;
    const remindersEnabled =
      (aptId != null ? (apartmentRemindersById.get(aptId) ?? null) : null) ??
      propertyRemindersEnabled ??
      true;
    if (!remindersEnabled) continue;
    trackedIds.add(b.id);
    trackedBookings.push(b);
    rentDueDayByBooking.set(b.id, b.rent_due_date ?? null);
  }

  let pastMonths = 0;
  if (trackedIds.size) {
    try {
      const { ledger, proofFor } = await fetchRentLedgerForBookings(
        supabase,
        Array.from(trackedIds)
      );
      const currentRows = currentPeriodLedger(ledger, rentDueDayByBooking, now);
      for (const b of trackedBookings) {
        if (isPaidCurrentPeriod(b, now)) continue;
        const current = currentRows.get(b.id);
        if (!current) continue;
        const stage = computeCurrentStage(daysSince(current.dueDate, now));
        if (stage > 0) overdue++;
        if (stage >= 4) inkasso++;
      }
      pastMonths = selectOpenPastMonthRent({
        rows: ledger,
        proofFor,
        rentDueDayByBooking,
        bookingIds: trackedIds,
        now,
      }).length;
    } catch (e) {
      // Fail closed on overdue counts: without the invoice row we cannot know
      // the concrete due date (especially for the prorated first rent).
      console.warn("past-month ledger load failed:", e);
    }
  }

  return { overdue, pastMonths, inkasso, total: overdue + pastMonths };
}

export function usePropertyReminderStats(
  propertyId: number | null,
  opts: {
    enabled?: boolean;
    /** Pass when the caller already holds `properties.rent_reminders_enabled`
     *  (saves a query); omit to have the hook fetch it. */
    propertyRemindersEnabled?: boolean | null;
  } = {}
) {
  return useQuery({
    queryKey: [PROPERTY_REMINDER_STATS_KEY, propertyId],
    enabled: propertyId != null && (opts.enabled ?? true),
    queryFn: () =>
      propertyId == null
        ? Promise.resolve(EMPTY)
        : fetchPropertyReminderStats(propertyId, {
            propertyRemindersEnabled: opts.propertyRemindersEnabled,
          }),
    staleTime: 30_000,
  });
}

/**
 * Badge totals for every given property, keyed by property id, once ALL of
 * them have settled; `undefined` until then (a failed property counts as 0).
 *
 * For callers that must rank a whole list by reminders before paginating
 * (the admin properties grid) rather than lazily per visible card. Uses the
 * SAME query key and fetcher as `usePropertyReminderStats`, so each card's own
 * hook reads the entry this one already filled — no second fetch — and every
 * `invalidateQueries({ queryKey: [PROPERTY_REMINDER_STATS_KEY] })` after a
 * reminder is sent or a payment marked paid refreshes this too.
 *
 * Publishing only a COMPLETE set is deliberate: N independent queries
 * resolving one by one would otherwise reshuffle the grid N times as each
 * count landed, moving cards between pages under the operator.
 *
 * That has to hold on refreshes too, not just the first load — a refetch of
 * an already-cached query is `isFetching`, NOT `isPending`, so gating on
 * `isPending` alone would publish a mixed old/new set query-by-query on every
 * window-focus refetch past `staleTime` and after each reminder-sent /
 * marked-paid invalidation. While anything is in flight we therefore keep
 * serving the last complete snapshot, and only fall back to `undefined` when
 * there is no snapshot for exactly this set of properties — a snapshot taken
 * before a search/city filter changed describes the wrong list.
 */
export function useAllPropertiesReminderCounts(
  properties: Array<Pick<PropertyListItem, "id" | "rent_reminders_enabled">>
): Record<number, number> | undefined {
  const complete = useQueries({
    queries: properties.map((p) => ({
      queryKey: [PROPERTY_REMINDER_STATS_KEY, p.id],
      queryFn: () =>
        fetchPropertyReminderStats(p.id, {
          propertyRemindersEnabled: p.rent_reminders_enabled,
        }),
      staleTime: 30_000,
    })),
    combine: (results) => {
      if (results.some((r) => r.isPending || r.isFetching)) return undefined;
      const totalById: Record<number, number> = {};
      results.forEach((r, i) => {
        totalById[properties[i].id] = r.data?.total ?? 0;
      });
      return totalById;
    },
  });

  // Written during render purely as a cache of the last value this hook
  // returned; it never drives a render of its own, so a double render in
  // StrictMode just rewrites the same snapshot.
  const lastComplete = useRef<Record<number, number> | undefined>(undefined);
  if (complete) lastComplete.current = complete;
  if (complete) return complete;

  const previous = lastComplete.current;
  if (!previous) return undefined;
  const coversCurrentList =
    Object.keys(previous).length === properties.length &&
    properties.every((p) => p.id in previous);
  return coversCurrentList ? previous : undefined;
}
